'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
  icon: string;
  category: string;
}

interface TimelineSectionProps {
  events: TimelineEvent[];
}

const TimelineSection: React.FC<TimelineSectionProps> = ({ events }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', ...Array.from(new Set(events.map(e => e.category)))];

  const filteredEvents = selectedCategory === 'All' 
    ? events 
    : events.filter(e => e.category === selectedCategory);

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-4">
            Movement Milestones
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            The journey from vision to global movement
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full font-cta text-sm font-bold transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-card text-foreground hover:bg-muted border border-border'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Timeline */}
        <div className="relative">
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-border transform -translate-x-1/2" />

          <div className="space-y-12">
            {filteredEvents.map((event, index) => (
              <div
                key={index}
                className={`relative grid lg:grid-cols-2 gap-8 items-center ${
                  index % 2 === 0 ? '' : 'lg:flex-row-reverse'
                }`}
              >
                <div className={`${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:pl-12 lg:col-start-2'}`}>
                  <div className="bg-card p-6 rounded-xl shadow-md border border-border hover:shadow-lg transition-shadow duration-300">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="text-2xl font-headline font-bold text-primary">{event.year}</span>
                      <span className="px-3 py-1 bg-accent/20 rounded-full text-xs font-cta font-bold text-accent-foreground">
                        {event.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-headline font-bold text-foreground mb-3">
                      {event.title}
                    </h3>
                    <p className="text-muted-foreground font-body leading-relaxed">
                      {event.description}
                    </p>
                  </div>
                </div>

                <div className={`${index % 2 === 0 ? 'lg:col-start-2' : 'lg:col-start-1 lg:row-start-1'} flex justify-center`}>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg z-10">
                    <Icon name={event.icon as any} size={28} className="text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;